import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from './useCurrentUser';

export interface ReactionSummary {
  /** Total number of likes (content "+" or "") */
  likes: number;
  /** Total number of reactions of all types */
  total: number;
  /** Whether the current logged-in user has liked this event */
  liked: boolean;
  /** Map of reaction content → count (for emoji reactions) */
  reactions: Record<string, number>;
}

export interface ReplySummary {
  /** Number of direct replies (kind 1 with e tag pointing to this event) */
  count: number;
}

/**
 * Fetches reactions (kind 7) and replies (kind 1) for a given event.
 * Combines both into a single relay query for efficiency.
 */
export function useEventReactions(eventId: string | undefined, eventPubkey?: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<{ reactions: ReactionSummary; replies: ReplySummary }>({
    queryKey: ['event-interactions', eventId ?? ''],
    queryFn: async (c) => {
      if (!eventId) return { reactions: { likes: 0, total: 0, liked: false, reactions: {} }, replies: { count: 0 } };

      // Fetch reactions (kind 7) and replies (kind 1) in a single round-trip
      const events = await nostr.query(
        [
          { kinds: [7], '#e': [eventId], limit: 100 },
          { kinds: [1], '#e': [eventId], limit: 100 },
        ],
        { signal: c.signal ?? AbortSignal.timeout(3000) },
      );

      const reactionEvents = events.filter((e) => e.kind === 7);
      const replyEvents = events.filter((e) => e.kind === 1);

      // Build reaction summary
      const reactions: Record<string, number> = {};
      let likes = 0;
      let liked = false;
      const userPubkey = user?.pubkey;

      for (const re of reactionEvents) {
        const content = re.content || '+';

        // Count likes specifically (content is "+" or empty)
        if (content === '+' || content === '') {
          likes++;
        }

        // Track all reaction types
        reactions[content] = (reactions[content] || 0) + 1;

        // Check if current user liked this
        if (userPubkey && re.pubkey === userPubkey) {
          liked = true;
        }
      }

      return {
        reactions: {
          likes,
          total: reactionEvents.length,
          liked,
          reactions,
        },
        replies: {
          count: replyEvents.length,
        },
      };
    },
    enabled: !!eventId,
    staleTime: 30 * 1000, // 30 seconds
  });
}
